import { useState } from 'react'
import { Camera, Check, Eye, EyeOff, KeyRound, Loader2, ShieldCheck, User as UserIcon } from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import { useAppAuth } from '@/contexts/AuthContext'
import { hashPassword, maskCPF, nowISO, verifyPassword } from '@/utils/masks'

const roleLabels: Record<string, string> = {
  ADMIN: 'Administrador',
  MODERADOR: 'Moderador',
  GESTOR: 'Gestor',
  COORDENADOR: 'Coordenador',
}

function MeuPerfil() {
  const { user, refreshUser } = useAppAuth()

  const [fotoUrl, setFotoUrl] = useState(user?.fotoUrl || '')
  const [uploading, setUploading] = useState(false)
  const [salvandoFoto, setSalvandoFoto] = useState(false)

  const [senhaAtual, setSenhaAtual] = useState('')
  const [novaSenha, setNovaSenha] = useState('')
  const [confirmarSenha, setConfirmarSenha] = useState('')
  const [mostrarSenhas, setMostrarSenhas] = useState(false)
  const [salvandoSenha, setSalvandoSenha] = useState(false)
  const [erroSenha, setErroSenha] = useState('')

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file)
      return
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione um ficheiro de imagem.')
      return
    }
    setUploading(true)
    try {
      const results = await lumi.tools.file.upload([file])
      const result = results?.[0]
      if (result?.fileUrl)
        setFotoUrl(result.fileUrl)
      else
        toast.error(result?.uploadError || 'Erro ao enviar a foto.')
    }
    catch {
      toast.error('Erro ao enviar a foto.')
    }
    finally {
      setUploading(false)
    }
  }

  const salvarFoto = async () => {
    if (!user?._id)
      return
    setSalvandoFoto(true)
    try {
      await (lumi.entities as any).user_profiles.update(user._id, {
        fotoUrl,
        updatedAt: nowISO(),
      })
      await refreshUser()
      toast.success('Foto atualizada com sucesso.')
    }
    catch {
      toast.error('Não foi possível guardar a foto.')
    }
    finally {
      setSalvandoFoto(false)
    }
  }

  const salvarSenha = async () => {
    setErroSenha('')
    if (!user?._id)
      return

    if (!senhaAtual) {
      setErroSenha('Informe a sua senha atual.')
      return
    }
    if (novaSenha.length < 6) {
      setErroSenha('A nova senha deve ter no mínimo 6 caracteres.')
      return
    }
    if (novaSenha !== confirmarSenha) {
      setErroSenha('As senhas não coincidem.')
      return
    }
    if (novaSenha === senhaAtual) {
      setErroSenha('A nova senha deve ser diferente da atual.')
      return
    }

    setSalvandoSenha(true)
    try {
      const r = await (lumi.entities as any).user_profiles.list({
        filter: { userId: user.userId },
        limit: 1,
      })
      const atual = r?.list?.[0]
      const valida = await verifyPassword(senhaAtual, atual?.passwordHash || '')
      if (!valida) {
        setErroSenha('A senha atual está incorreta.')
        return
      }

      await (lumi.entities as any).user_profiles.update(user._id, {
        passwordHash: await hashPassword(novaSenha),
        updatedAt: nowISO(),
      })

      try {
        await (lumi.entities as any).protocolo_historico.create({
          protocoloId: `user:${user._id}`,
          acao: 'ALTERACAO_SENHA',
          userId: user.userId,
          descricao: `O utilizador ${user.nomeCompleto} alterou a própria senha.`,
          creator: user.userId,
          createdAt: nowISO(),
        })
      }
      catch {
        /* histórico é opcional */
      }

      setSenhaAtual('')
      setNovaSenha('')
      setConfirmarSenha('')
      toast.success('Senha alterada com sucesso.')
    }
    catch {
      toast.error('Não foi possível alterar a senha.')
    }
    finally {
      setSalvandoSenha(false)
    }
  }

  if (!user)
    return null

  const fotoAlterada = (user.fotoUrl || '') !== fotoUrl

  return (
    <div className="max-w-[720px] flex flex-col gap-4 sm:gap-5">
      <div>
        <h1 className="text-[20px] sm:text-[22px] font-semibold text-[#12141A] leading-tight">Meu Perfil</h1>
        <p className="text-[12.5px] sm:text-[13.5px] text-[#6B7280] mt-1">
          Pode alterar apenas a sua foto e a sua senha. Os restantes dados são geridos pelo administrador.
        </p>
      </div>

      {/* Identificação */}
      <div className="bg-white rounded-2xl border border-[#E7E9EC] p-4 sm:p-5">
        <div className="flex items-center gap-4">
          <div className="relative shrink-0">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#EEF3FE] overflow-hidden flex items-center justify-center border border-[#DCE3F0]">
              {fotoUrl
                ? <img src={fotoUrl} alt={user.nomeCompleto} className="w-full h-full object-cover" />
                : <UserIcon className="w-7 h-7 text-[#1E4FBF]" strokeWidth={1.6} />}
            </div>
            <label className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#1E4FBF] text-white flex items-center justify-center cursor-pointer hover:bg-[#1A44A6] transition-colors">
              {uploading
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2} />
                : <Camera className="w-3.5 h-3.5" strokeWidth={2} />}
              <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
            </label>
          </div>
          <div className="min-w-0">
            <p className="text-[15px] font-semibold text-[#12141A] truncate">{user.nomeCompleto}</p>
            <p className="text-[12.5px] text-[#6B7280] mt-0.5">
              {roleLabels[user.role] || user.role}
              {' '}
              • CPF
              {' '}
              {maskCPF(user.cpf)}
            </p>
            {user.funcao && <p className="text-[12px] text-[#9CA3AF] mt-0.5 truncate">{user.funcao}</p>}
          </div>
        </div>

        {fotoAlterada && (
          <div className="flex items-center gap-2 mt-4">
            <button
              onClick={salvarFoto}
              disabled={salvandoFoto || uploading}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#1E4FBF] hover:bg-[#1A44A6] text-white text-[13px] font-medium disabled:opacity-60"
            >
              {salvandoFoto ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2} /> : <Check className="w-3.5 h-3.5" strokeWidth={2.2} />}
              Guardar foto
            </button>
            <button
              onClick={() => setFotoUrl(user.fotoUrl || '')}
              className="px-3.5 py-2 rounded-lg border border-[#D1D5DB] text-[13px] font-medium text-[#4B5563] hover:bg-[#F5F6F8]"
            >
              Cancelar
            </button>
          </div>
        )}
      </div>

      {/* Senha */}
      <div className="bg-white rounded-2xl border border-[#E7E9EC] p-4 sm:p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-[#EEF3FE] flex items-center justify-center">
            <KeyRound className="w-4 h-4 text-[#1E4FBF]" strokeWidth={1.8} />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-[#12141A]">Alterar senha</h2>
            <p className="text-[11.5px] text-[#9CA3AF]">Confirme a senha atual antes de definir a nova.</p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Senha atual</label>
            <input
              type={mostrarSenhas ? 'text' : 'password'}
              value={senhaAtual}
              onChange={e => setSenhaAtual(e.target.value)}
              autoComplete="current-password"
              className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Nova senha</label>
              <input
                type={mostrarSenhas ? 'text' : 'password'}
                value={novaSenha}
                onChange={e => setNovaSenha(e.target.value)}
                autoComplete="new-password"
                className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
              />
            </div>
            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Confirmar nova senha</label>
              <input
                type={mostrarSenhas ? 'text' : 'password'}
                value={confirmarSenha}
                onChange={e => setConfirmarSenha(e.target.value)}
                autoComplete="new-password"
                className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={() => setMostrarSenhas(v => !v)}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[#6B7280] hover:text-[#12141A] w-fit"
          >
            {mostrarSenhas ? <EyeOff className="w-3.5 h-3.5" strokeWidth={1.9} /> : <Eye className="w-3.5 h-3.5" strokeWidth={1.9} />}
            {mostrarSenhas ? 'Ocultar senhas' : 'Mostrar senhas'}
          </button>

          {erroSenha && <p className="text-[12.5px] text-[#B91C1C]">{erroSenha}</p>}

          <div className="flex items-center justify-between gap-3 pt-1">
            <p className="hidden sm:flex items-center gap-1.5 text-[11.5px] text-[#9CA3AF]">
              <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.9} />
              Mínimo de 6 caracteres.
            </p>
            <button
              onClick={salvarSenha}
              disabled={salvandoSenha}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#1E4FBF] hover:bg-[#1A44A6] text-white text-[13.5px] font-medium disabled:opacity-60 w-full sm:w-auto"
            >
              {salvandoSenha && <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2} />}
              {salvandoSenha ? 'A guardar...' : 'Alterar senha'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MeuPerfil
